import * as React from 'react';
import { emphasize, styled } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import {
  Box, Tab, Tabs, useTheme,
} from '@mui/material';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor = '#00';
  return {
    backgroundColor,
    height: theme.spacing(5),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    borderRadius: '10px',
    transition: '0.5s',
    cursor: 'pointer',
    '&:hover, &:focus': {
      backgroundColor: emphasize(theme.palette.primary.main, 0.06),
      color: 'white',
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(theme.palette.primary.main, 0.12),
      color: 'white',
    },
  };
}) as typeof Chip; // TypeScript only: need a type cast here because https://github.com/Microsoft/TypeScript/issues/26591

interface CustomizedBreadcrumbsProps{
  elements: { label: string, hash: string, icon: any }[],
  separator: string
  currentSection: string
  onClick: (hash: string) => void
  isMobile?: boolean
}

export default function CustomizedBreadcrumbs(props: CustomizedBreadcrumbsProps) {
  const {
    elements, separator, currentSection, onClick, isMobile,
  } = props;
  const theme = useTheme();
  const activeIndexRaw = elements.findIndex((el) => el.hash === currentSection);
  const activeIndex = activeIndexRaw === -1 ? 0 : activeIndexRaw;

  if (isMobile) {
    return (
      <Box sx={{ width: '100%', mt: 0.5 }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => {
            const el = elements[value];
            if (el) onClick(el.hash);
          }}
          variant="fullWidth"
          aria-label="breadcrumb"
          sx={{
            minHeight: 48,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              minHeight: 48,
              py: 0,
              textTransform: 'none',
              fontSize: theme.typography.body2.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
            },
          }}
        >
          {elements.map((el) => (el.icon ? (
            <Tab key={el.hash} label={el.label} icon={el.icon} iconPosition="start" />
          ) : (
            <Tab key={el.hash} label={el.label} />
          )))}
        </Tabs>
      </Box>
    );
  }

  return (
    <div style={{ marginLeft: 5 }}>
      <Breadcrumbs separator={separator} aria-label="breadcrumb">
        {elements.map((el) => (
          <StyledBreadcrumb
            key={el.hash}
            style={{
              backgroundColor: currentSection === el.hash
                ? theme.palette.primary.main : undefined,
              color: currentSection === el.hash
                ? 'white' : undefined,
            }}
            component="a"
            label={el.label}
            icon={el.icon}
            onClick={() => onClick(el.hash)}
          />
        ))}
      </Breadcrumbs>
    </div>
  );
}

CustomizedBreadcrumbs.defaultProps = {
  isMobile: false,
};
